import React, {useRef, useState} from 'react';
import {useForm} from "react-hook-form";
import {NavLink, useNavigate} from "react-router-dom";
import {document} from "../../../models/document";
import {documentHydrator} from "../../../models/hydrator/documentHydrator";
import {Require} from "../Require";
import {ApiError, ApiService} from "../../../service/ApiService";
import {UploadService} from "../../../service/UploadService";
import {scrollTo} from "../../../service/ToolsService";

// Taille maximale acceptée par l'API (2 Mo)
const MAX_SIZE: number = 2 * 1024 * 1024;

type FormDataType = {
  label: string;
  file?: string;
  lineBreak: boolean;
  enabled: boolean;
}

type DocumentFormProps = {
  document?: document,
}

export const DocumentForm = ({document}: DocumentFormProps) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState(undefined as undefined | string);
  const [filename, setFilename] = useState(undefined as undefined | string);
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: {errors}
  } = useForm<FormDataType>({
    defaultValues: {
      label: document?.label,
      lineBreak: document?.lineBreak ?? false,
      enabled: document?.enabled ?? true,
    }
  });

  const handleFile = async (f?: File): Promise<void> => {
    if (!f) {
      return;
    }

    if (f.type !== 'application/pdf') {
      setError('file', {type: 'manual', message: 'Le fichier doit être un PDF'});
      return;
    }

    if (f.size > MAX_SIZE) {
      setError('file', {type: 'manual', message: 'Le fichier ne doit pas dépasser 2 Mo'});
      return;
    }

    clearErrors('file');
    setFilename(f.name);
    setFile(await UploadService.handleFileToBase64(f));
  }

  const onSubmit = async (data: FormDataType) => {
    if (!document && !file) {
      setError('file', {type: 'manual', message: 'Le fichier est requis'});
      return;
    }

    const d = {
      label: data.label,
      file: file,
      lineBreak: data.lineBreak,
      enabled: data.enabled,
    };

    try {
      if (document) {
        await ApiService.patch(`/document/${document.documentId}`, { param: () => d })
      } else {
        await ApiService.post('/document', { param: () => d })
      }

      navigate('/admin/document')
      scrollTo();
    } catch (e: any) {
      const error: ApiError = e as ApiError;

      error.errors?.forEach((e: {
        field: string,
        message: string
      }) => {
        setError(e.field as "label" | "file" | "lineBreak" | "enabled", {
          type: "manual",
          message: e.message
        })
      });
    }
  }

  return (
    <>
      <form className="p-3" method="post" onSubmit={handleSubmit(onSubmit)}>
        <div className="col max-w600px">
          <div className="form-check form-switch mb-3">
            <input className="form-check-input" type="checkbox" id="form-enabled" {...register('enabled')} />
            <label className="form-check-label" htmlFor="form-enabled">
              Le document est {`${watch('enabled') ? 'visible' : 'masqué'}`}
            </label>
          </div>
          <div className="form-check form-switch mb-3">
            <input className="form-check-input" type="checkbox" id="form-line-break" {...register('lineBreak')} />
            <label className="form-check-label" htmlFor="form-line-break">
              Commencer une nouvelle ligne avec ce document
            </label>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="form-label">Nom <Require/></label>
            <input id="form-label" type="text" className="form-control" placeholder="~" {...register('label', {
              required: 'Le nom est requis',
              validate: {
                maxLength: (value) =>
                  (value ?? '').length <= 255 || 'Le nom ne doit pas dépasser 255 caractères',
              },
            })} />
            {errors.label && <p className="text-danger">{errors.label.message}</p>}
          </div>
          <div className="mb-3">
            <label className="form-label d-block">Fichier PDF (2 Mo max.) {!document && <Require/>}</label>
            <input ref={inputRef} type="file" accept="application/pdf" hidden
                   onChange={(e) => {
                     handleFile(e.target.files?.[0]);
                     e.target.value = '';
                   }}/>
            <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => inputRef.current?.click()}>
              <i className="fa-solid fa-upload me-1"/> {document ? 'Remplacer le fichier' : 'Choisir un fichier'}
            </button>
            {filename && (
              <span><i className="fa-regular fa-file-pdf me-1"/>{filename}</span>
            )}
            {!filename && document && (
              <a href={documentHydrator.downloadUrl(document)} target="_blank" rel="noreferrer">
                <i className="fa-regular fa-file-pdf me-1"/>{document.slug}.pdf
              </a>
            )}
            {errors.file && <p className="text-danger">{errors.file.message}</p>}
          </div>
        </div>

        <div className="mt-3">
          <button className="btn btn-sm btn-success me-1" type="submit">Enregistrer</button>
          <NavLink to={`/admin/document`} onClick={scrollTo} className="btn btn-sm btn-info">Retour aux documents</NavLink>
        </div>
      </form>
    </>
  );
}
